import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  updateDoc,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getDb, functions, auth, getCallableName } from './firebase';
import type { User, NotificationSettings } from '../types';

const PAGE_SIZE = 20;

/**
 * Fetch display names for a list of user IDs.
 * Returns a map of userId → display name (falls back to truncated ID if not found).
 */
export async function getUserNames(userIds: string[]): Promise<Record<string, string>> {
  const unique = [...new Set(userIds)];
  const result: Record<string, string> = {};

  await Promise.all(
    unique.map(async userId => {
      try {
        const snap = await getDoc(doc(getDb(), 'users', userId));
        if (snap.exists()) {
          const data = snap.data();
          result[userId] = (data.name as string) || (data.email as string) || userId.slice(0, 8);
        } else {
          result[userId] = userId.slice(0, 8);
        }
      } catch {
        result[userId] = userId.slice(0, 8);
      }
    })
  );

  return result;
}

export interface ListUsersResult {
  items: User[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/**
 * List users with optional email search and cursor-based pagination.
 */
export async function listUsers(
  cursor: QueryDocumentSnapshot<DocumentData> | null = null,
  searchQuery = ''
): Promise<ListUsersResult> {
  const colRef = collection(getDb(), 'users');
  const constraints = [];

  if (searchQuery.trim()) {
    // Search by email prefix using range query
    const term = searchQuery.trim().toLowerCase();
    constraints.push(where('email', '>=', term));
    constraints.push(where('email', '<=', term + '\uf8ff'));
    constraints.push(orderBy('email'));
  } else {
    constraints.push(orderBy('email'));
  }

  constraints.push(limit(PAGE_SIZE + 1));
  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  const q = query(colRef, ...constraints);
  const snap = await getDocs(q);

  const hasMore = snap.docs.length > PAGE_SIZE;
  const docs = hasMore ? snap.docs.slice(0, PAGE_SIZE) : snap.docs;
  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;

  const items: User[] = docs.map(d => ({
    id: d.id,
    ...(d.data() as Omit<User, 'id'>),
  }));

  return { items, lastDoc, hasMore };
}

/**
 * Update editable profile fields for a user.
 */
export async function updateUser(uid: string, data: Partial<Pick<User, 'name'>>): Promise<void> {
  await updateDoc(doc(getDb(), 'users', uid), data as DocumentData);
}

/**
 * Fetch notification settings for a user.
 * Returns null if no settings document exists.
 */
export async function getUserNotificationSettings(
  uid: string
): Promise<NotificationSettings | null> {
  const snap = await getDoc(doc(getDb(), 'user_notification_settings', uid));
  if (!snap.exists()) return null;
  return snap.data() as NotificationSettings;
}

/**
 * Update notification settings for a user.
 */
export async function updateNotificationSettings(
  uid: string,
  settings: Partial<NotificationSettings>
): Promise<void> {
  await updateDoc(doc(getDb(), 'user_notification_settings', uid), settings as DocumentData);
}

/**
 * Admin-initiated user deletion.
 * Calls the deleteUserAccount Cloud Function with the target uid.
 * Requires the caller to have the admin custom claim.
 */
export async function deleteUserAdmin(uid: string): Promise<void> {
  const fn = httpsCallable(functions, getCallableName('deleteUserAccount'));
  await fn({ uid });
}

/**
 * Grant or revoke admin access for a user.
 * Calls the setUserAdminStatus Cloud Function with the target uid and desired state.
 * Requires the caller to have the admin custom claim.
 * Callers cannot change their own admin status.
 */
export async function setUserAdminStatus(uid: string, isAdmin: boolean): Promise<void> {
  const fn = httpsCallable(functions, getCallableName('setUserAdminStatus'));
  await fn({ uid, isAdmin });
}

/**
 * Send a password reset email to a user's email address.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

export async function countUsers(): Promise<number> {
  const snap = await getCountFromServer(collection(getDb(), 'users'));
  return snap.data().count;
}
